/* eslint-disable no-restricted-imports */
import type { AdaptiveLogger } from "@1js/office-online-jsapi-core";
import type { DataBoundary } from "@1js/owl-service";
import { getHealthEndpoint, getRemoteUlsEndpoint } from "@1js/owl-service";
import type { WiseEnvironment } from "@1js/wise-environments";
import {
  LogCategory,
  TraceLevel,
  getAbsoluteTimeSinceEpoch,
} from "@1js/office-online-jsapi-core";
import { getDataBoundaryForRegion } from "@1js/owl-service";
import type {
  Health,
  HealthConfig,
  HealthDimensions,
} from "@1js/office-online-qos-small";
import type { DocumentBootInfo, SessionConfig, UiHostConfig } from "../types";

// Find data boundary of given region and updates logger accordingly
export function updateLoggerDataBoundaryUsingRegion(
  region: string,
  wiseEnv: WiseEnvironment,
  logger?: AdaptiveLogger,
  health?: Health,
  sessionId?: string,
  documentBootInfo?: DocumentBootInfo,
  sessionConfig?: SessionConfig,
  uiHostConfig?: UiHostConfig
): void {
  const dataBoundary = getDataBoundaryForRegion(region || "");
  updateLoggerWithDataBoundary(
    dataBoundary,
    wiseEnv,
    logger,
    health,
    sessionId,
    documentBootInfo,
    sessionConfig,
    uiHostConfig
  );
}

/*
 * Trigger updateLoggerWithDataBoundary after retrieved the data boundary from msg channel.
 * Remote telemetry endpoint will be set correctly and start flushing data points.
 */
function updateLoggerWithDataBoundary(
  dataBoundary: DataBoundary,
  wiseEnvironment: WiseEnvironment,
  logger?: AdaptiveLogger,
  health?: Health,
  sessionId?: string,
  documentBootInfo?: DocumentBootInfo,
  sessionConfig?: SessionConfig,
  uiHostConfig?: UiHostConfig
): void {
  // Update BULS endpoint and flush cached logs, periodically auto flush afterwards.
  if (logger) {
    logger.ULS.sendTraceTag(
      0x1e3528a0 /* tag_4ns86 */,
      LogCategory.msoulscat_Wac_UnifiedUiHost,
      TraceLevel.Info,
      `Start updating BULS endpoint with data boundary, timestamp: ${getAbsoluteTimeSinceEpoch()}`
    );
    const dataBoundaryAwareEndpoint = getRemoteUlsEndpoint(
      wiseEnvironment,
      dataBoundary
    );
    logger.updateEndpointAndFlush(dataBoundaryAwareEndpoint);
  }

  // Start flush cached health event by initialize the health instance with correct data boundary.
  if (health) {
    const dataBoundaryAwareEndpoint = getHealthEndpoint(
      wiseEnvironment,
      sessionId,
      dataBoundary
    );

    const dimensions: HealthDimensions = {
      application: documentBootInfo?.fileType,
      applicationMode: "3PUnifiedUiHost",
      applicationModeExtended: "3P_UNIFIEDUIHOST",
      session_id: sessionId,
      host: "3P",
      sessionOrigin: documentBootInfo?.wdOrigin,
      tenantId: sessionConfig?.tenantId,
      uiHost: uiHostConfig?.uiHostName,
      uiHostIntegrationType: uiHostConfig?.uiHostIntegrationType,
    };

    health.initialize({
      isHealthEnabled: true,
      isOfflineEnabled: false,
      remoteTelemetryUrl: dataBoundaryAwareEndpoint,
      dimensions,
    } as HealthConfig);

    health.addOrUpdatePartCDimension(
      "UUIHTargetApp",
      documentBootInfo?.fileType
    );
    health.addOrUpdatePartCDimension(
      "HealthSourceComponent",
      "3PUnifiedUiHost"
    );
    health.addOrUpdatePartCDimension(
      "HostCorrelationId",
      sessionConfig?.hostCorrelationId
    );
  }
}
